(function () {
  const TICKER_SELECTOR = '.chakrikka-trust-ticker';
  const TRACK_SELECTOR = '.chakrikka-trust-ticker__track';
  const GROUP_SELECTOR = '.chakrikka-trust-ticker__group';
  const MIN_TRANSITION_DURATION = 250;
  const MAX_FRAME_GAP = 50;

  class ChakrikkaTrustTickerSlowdown {
    constructor(ticker) {
      this.ticker = ticker;
      this.track = ticker.querySelector(TRACK_SELECTOR);
      this.firstGroup = ticker.querySelector(GROUP_SELECTOR);
      this.frame = null;
      this.activePointerId = null;
      this.position = 0;
      this.loopDistance = 0;
      this.lastFrameTime = null;
      this.currentRate = 1;
      this.targetRate = 1;
      this.rateStart = 1;
      this.rateTransitionStart = null;
      this.rateTransitionDuration = 0;
      this.isVisible = true;
      this.slowRate = this.readNumber(ticker.dataset.slowRate, 0.2, 0.05, 0.95);
      this.tickerDuration = this.readNumber(ticker.dataset.tickerDuration, 35, 1, 300);
      this.transitionDuration = this.readNumber(ticker.dataset.speedTransition, 1400, 0, 5000);
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.tick = this.tick.bind(this);
      this.measure = this.measure.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerEnd = this.onPointerEnd.bind(this);
      this.onWindowBlur = this.onWindowBlur.bind(this);
      this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);

      if (!this.track || !this.firstGroup) return;

      this.track.style.animation = 'none';
      this.measure();

      this.ticker.addEventListener('pointerenter', this.onPointerEnter);
      this.ticker.addEventListener('pointerleave', this.onPointerLeave);
      this.ticker.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      window.addEventListener('pointerup', this.onPointerEnd, { passive: true });
      window.addEventListener('pointercancel', this.onPointerEnd, { passive: true });
      window.addEventListener('blur', this.onWindowBlur);

      if (typeof this.prefersReducedMotion.addEventListener === 'function') {
        this.prefersReducedMotion.addEventListener('change', this.onMotionPreferenceChange);
      } else {
        this.prefersReducedMotion.addListener(this.onMotionPreferenceChange);
      }

      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(this.measure);
        this.resizeObserver.observe(this.firstGroup);
      } else {
        window.addEventListener('resize', this.measure);
      }

      if (typeof IntersectionObserver === 'function') {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          this.isVisible = entries[0]?.isIntersecting ?? true;
          if (this.isVisible) this.startMotion();
          else this.stopMotion();
        });
        this.intersectionObserver.observe(this.ticker);
      }

      this.onMotionPreferenceChange();
    }

    readNumber(value, fallback, min, max) {
      const parsed = Number.parseFloat(value);
      if (!Number.isFinite(parsed)) return fallback;
      return Math.min(max, Math.max(min, parsed));
    }

    measure() {
      this.loopDistance = this.firstGroup.getBoundingClientRect().width;
      if (this.loopDistance > 0) this.position %= this.loopDistance;
    }

    startMotion() {
      if (this.frame !== null || !this.isVisible || this.prefersReducedMotion.matches) return;
      this.lastFrameTime = null;
      this.frame = window.requestAnimationFrame(this.tick);
    }

    stopMotion() {
      if (this.frame !== null) window.cancelAnimationFrame(this.frame);
      this.frame = null;
      this.lastFrameTime = null;
    }

    tick(time) {
      this.frame = null;
      if (!this.isVisible || this.prefersReducedMotion.matches) return;

      if (this.lastFrameTime !== null && this.loopDistance > 0) {
        const elapsed = Math.min(Math.max(time - this.lastFrameTime, 0), MAX_FRAME_GAP);
        const pixelsPerSecond = (this.loopDistance * 2) / this.tickerDuration;
        this.updateRate(time);
        this.position = (this.position + pixelsPerSecond * this.currentRate * (elapsed / 1000)) % this.loopDistance;
        this.track.style.transform = `translate3d(${-this.position}px, 0, 0)`;
      }

      this.lastFrameTime = time;
      this.frame = window.requestAnimationFrame(this.tick);
    }

    updateRate(time) {
      if (this.rateTransitionStart === null) return;

      const elapsed = Math.min((time - this.rateTransitionStart) / this.rateTransitionDuration, 1);
      const eased = elapsed < 0.5 ? 4 * elapsed ** 3 : 1 - Math.pow(-2 * elapsed + 2, 3) / 2;
      this.currentRate = this.rateStart + (this.targetRate - this.rateStart) * eased;

      if (elapsed === 1) {
        this.currentRate = this.targetRate;
        this.rateTransitionStart = null;
      }
    }

    transitionTo(targetRate) {
      if (this.prefersReducedMotion.matches) return;
      if (targetRate === this.targetRate && this.rateTransitionStart !== null) return;

      const distance = Math.abs(targetRate - this.currentRate);
      if (distance < 0.001) {
        this.currentRate = targetRate;
        this.targetRate = targetRate;
        this.rateTransitionStart = null;
        return;
      }

      const fullDistance = Math.max(1 - this.slowRate, Number.EPSILON);
      this.rateStart = this.currentRate;
      this.targetRate = targetRate;
      this.rateTransitionStart = window.performance.now();
      this.rateTransitionDuration = Math.max(
        MIN_TRANSITION_DURATION,
        this.transitionDuration * Math.min(distance / fullDistance, 1)
      );
    }

    onPointerEnter(event) {
      if (event.pointerType === 'mouse') this.transitionTo(this.slowRate);
    }

    onPointerLeave(event) {
      if (event.pointerType === 'mouse') this.transitionTo(1);
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse') return;
      this.activePointerId = event.pointerId;
      this.transitionTo(this.slowRate);
    }

    onPointerEnd(event) {
      if (event.pointerId !== this.activePointerId) return;
      this.activePointerId = null;
      this.transitionTo(1);
    }

    onWindowBlur() {
      this.activePointerId = null;
      this.transitionTo(1);
    }

    onMotionPreferenceChange() {
      if (this.prefersReducedMotion.matches) {
        this.stopMotion();
        this.position = 0;
        this.track.style.transform = 'translate3d(0, 0, 0)';
      } else {
        this.startMotion();
      }
    }

    destroy() {
      if (!this.track) return;
      this.stopMotion();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();

      this.ticker.removeEventListener('pointerenter', this.onPointerEnter);
      this.ticker.removeEventListener('pointerleave', this.onPointerLeave);
      this.ticker.removeEventListener('pointerdown', this.onPointerDown);
      window.removeEventListener('pointerup', this.onPointerEnd);
      window.removeEventListener('pointercancel', this.onPointerEnd);
      window.removeEventListener('blur', this.onWindowBlur);
      window.removeEventListener('resize', this.measure);

      if (typeof this.prefersReducedMotion.removeEventListener === 'function') {
        this.prefersReducedMotion.removeEventListener('change', this.onMotionPreferenceChange);
      } else {
        this.prefersReducedMotion.removeListener(this.onMotionPreferenceChange);
      }
    }
  }

  const instances = new WeakMap();

  function init(root) {
    const tickers = [];
    if (root.matches?.(TICKER_SELECTOR)) tickers.push(root);
    root.querySelectorAll?.(TICKER_SELECTOR).forEach((ticker) => tickers.push(ticker));

    tickers.forEach((ticker) => {
      if (!instances.has(ticker)) instances.set(ticker, new ChakrikkaTrustTickerSlowdown(ticker));
    });
  }

  function destroy(root) {
    const ticker = root.matches?.(TICKER_SELECTOR) ? root : root.querySelector?.(TICKER_SELECTOR);
    const instance = ticker ? instances.get(ticker) : null;
    if (!instance) return;
    instance.destroy();
    instances.delete(ticker);
  }

  if (window.ChakrikkaTrustTicker) {
    window.ChakrikkaTrustTicker.init(document);
    return;
  }

  window.ChakrikkaTrustTicker = { init, destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
